/**
 * Os dados do caso no cabeçalho da mesa (E205.F1.T03, ADR-0147 §7): instituição, parte, data do ato,
 * versão do exame e pacote, em campos rotulados. Só o que o caso, o documento do centro e o pacote já
 * trazem; nada novo do servidor. O que falta vira "—", sem esconder o rótulo.
 */
import { textoDaVersao } from '@/components/DocumentoDaMesa/versaoDoTexto';
import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData } from '@/lib/cases';
import type { DocumentoDaSaidaData } from '@/lib/documentoDaSaida';
import { dataBr } from '@/lib/formalizacao';
import { mascararCpf } from '@/lib/masks';
import type { LeituraDoPacote } from '@/lib/usePreparacaoDoPacote';

const t = ptBR.mesaUnica.dadosDoCaso;

export type CampoDoCaso = 'instituicao' | 'parte' | 'dataDoAto' | 'versao' | 'pacote';

export type DadosDoCaso = Readonly<Record<CampoDoCaso, string | null>>;

export const CAMPOS_DO_CASO: readonly CampoDoCaso[] = [
  'instituicao',
  'parte',
  'dataDoAto',
  'versao',
  'pacote',
];

function preenchido(valor: string | null | undefined): string | null {
  const limpo = valor?.trim() ?? '';
  return limpo === '' ? null : limpo;
}

function parte(caso: CaseRecordData | null): string | null {
  const nome = preenchido(caso?.parties);
  const cpf = caso?.cpf ? mascararCpf(caso.cpf) : null;
  if (nome !== null && cpf !== null) {
    return t.parteComCpf.replace('{nome}', nome).replace('{cpf}', cpf);
  }
  return nome;
}

function versao(documento: DocumentoDaSaidaData | null): string | null {
  if (documento === null || !documento.aplicavel) return null;
  const texto = textoDaVersao(documento.edicoes_sha256);
  if (documento.rodada === null) return texto;
  return t.versaoComTexto.replace('{rodada}', String(documento.rodada)).replace('{texto}', texto);
}

function pacote(leitura: LeituraDoPacote): string | null {
  if (leitura.estado === 'carregando') return t.pacoteLendo;
  if (leitura.estado === 'erro') return null;
  const dados = leitura.dados;
  if (dados.preparando) return t.pacotePreparando;
  if (dados.linhas.some((l) => l.estado === 'falhou' || l.estado === 'bloqueado')) {
    return t.pacoteComFalha;
  }
  if (!dados.completo) return t.pacoteIncompleto;
  return t.pacoteCompleto.replace('{versao}', String(dados.versao ?? '—'));
}

export function dadosDoCaso(entrada: {
  caso: CaseRecordData | null;
  bancoDoCaso: string | null | undefined;
  documento: DocumentoDaSaidaData | null;
  pacote: LeituraDoPacote;
}): DadosDoCaso {
  const dataDoAto = preenchido(entrada.caso?.data_do_ato);
  return {
    instituicao: preenchido(entrada.bancoDoCaso) ?? preenchido(entrada.caso?.requester),
    parte: parte(entrada.caso),
    dataDoAto: dataDoAto === null ? null : dataBr(dataDoAto),
    versao: versao(entrada.documento),
    pacote: pacote(entrada.pacote),
  };
}
